import { API_PATH } from "@constants/config";
import { request } from "@helper/http";

import twoFaTrustedToken from "@/helper/twoFaTrustedToken";
import {
  type IVerify2FaOtpPayload,
  type LoginAzureAdPayload,
  type LoginAzureAdResponse,
  type LoginPayload,
  type LoginResponse,
  type UserLoginSuccess,
} from "@/types/auth";
import { type UpdatedUserInput, type User } from "@/types/user";

export function getUserInfo(token: string): Promise<User> {
  return request({
    method: "GET",
    url: API_PATH.USER_INFO,
    headers: { Authorization: token },
  });
}

export function updateUserInfo(data: UpdatedUserInput): Promise<User> {
  return request({
    method: "patch",
    url: API_PATH.USER_INFO,
    data,
  });
}

export function login(payload: LoginPayload): Promise<LoginResponse> {
  const trustedDeviceToken = twoFaTrustedToken.get(payload.email);
  const data = {
    ...payload,
    trusted_device_token: trustedDeviceToken,
  };

  return request({
    method: "POST",
    url: API_PATH.LOGIN,
    data,
  });
}

export const loginAzureAd = (payload: LoginAzureAdPayload): Promise<LoginAzureAdResponse> => {
  return new Promise((resolve, reject) => {
    request({
      method: "POST",
      url: API_PATH.LOGIN_AZURE,
      data: payload,
    })
      .then((response) => {
        resolve({
          authorizationUrl: response.authorization_url,
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const loginAzureAdCallback = (payload: unknown): Promise<UserLoginSuccess> => {
  return request({
    method: "POST",
    url: API_PATH.LOGIN_AZURE_CALLBACK,
    data: payload,
  });
};

export const verify2FaOtp = (payload: IVerify2FaOtpPayload): Promise<UserLoginSuccess> => {
  return request({
    method: "POST",
    url: API_PATH.TWO_FA_VERIFY_OTP,
    data: payload,
  });
};

export const resend2FaOtp = (payload: Pick<IVerify2FaOtpPayload, "pre_2fa_token">): Promise<void> => {
  return request({
    method: "POST",
    url: API_PATH.TWO_FA_SEND_OTP,
    data: payload,
  });
};

export interface ForgetPasswordPayload {
  email: string;
}

export const forgetPassword = (data: ForgetPasswordPayload): Promise<void> => {
  return request({
    url: API_PATH.FORGET_PASSWORD,
    method: "POST",
    data,
  });
};

export interface ResetPasswordPayload {
  token: string;
  password: string;
}

export const resetPassword = (payload: ResetPasswordPayload): Promise<void> => {
  const { token, password } = payload;

  return request({
    url: API_PATH.RESET_PASSWORD,
    method: "POST",
    params: { token },
    data: { password },
  });
};

export const checkResetPasswordToken = (token: string): Promise<void> => {
  return request({
    url: API_PATH.RESET_PASSWORD,
    method: "GET",
    params: { token },
  });
};
